import {
  Breadcrumbs,
  Button,
  CircularProgress,
  Grid,
  Link,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import SortableTable from "../../../Components/SortableTable";
import Title from "../../../Components/Title";
import {
  getToken,
  getWarehouses,
  requestErrorHandler,
} from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { useStyles } from "../../../Utils/Styles";
import OrderDialog, { orderStatuses } from "./OrderDialog";

export default function ManufacturingOrders() {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [orders, setOrders] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [month, setMonth] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    getWarehouses(setWarehouses, dispatch);
  }, [dispatch]);

  const request = useCallback(
    async (path, payload = {}) => {
      const response = await fetch(EnvSettings.server + path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, token: getToken() }),
      });
      const result = await response.json();
      requestErrorHandler(result, dispatch);
      if (result.error || !response.ok)
        throw new Error(
          result.message || t("manufacturing.ordersRequestFailed"),
        );
      return result;
    },
    [dispatch, t],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const orderResult = await request("/get_manufacturing_orders");
      setOrders(orderResult.manufacturingOrders || []);
    } catch (err) {
      setError(err.message || t("manufacturing.ordersRequestFailed"));
    } finally {
      setLoading(false);
    }
  }, [request, t]);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const refreshSelected = async () => {
    try {
      const result = await request("/get_manufacturing_orders");
      setOrders(result.manufacturingOrders || []);
      setSelected(
        (current) =>
          (result.manufacturingOrders || []).find(
            (order) => order.id === current?.id,
          ) || current,
      );
    } catch (err) {
      setSaveError(err.message || t("manufacturing.ordersRequestFailed"));
    }
  };

  const save = async (order) => {
    setSaving(true);
    setSaveError("");
    try {
      await request(
        selected ? "/edit_manufacturing_order" : "/add_manufacturing_order",
        {
          order,
          ...(selected ? { manufacturingOrderId: selected.id } : {}),
        },
      );
      setOpen(false);
      setSelected(null);
      await refresh();
    } catch (err) {
      setSaveError(err.message || t("manufacturing.ordersRequestFailed"));
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(
    () =>
      orders.filter((order) => {
        if (warehouseId && String(order.WarehouseId) !== String(warehouseId))
          return false;
        if (month && !order.dueDate?.startsWith(month)) return false;
        if (status && order.status !== status) return false;
        const fields = [
          order.uuid,
          order.Warehouse?.name,
          order.Warehouse?.label,
          ...(order.ManufacturingOrderStockItems || []).flatMap((item) => [
            item.StockItem?.sku,
            item.StockItem?.name,
          ]),
        ];
        return fields.some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(search.trim().toLowerCase()),
        );
      }),
    [orders, warehouseId, month, status, search],
  );

  const today = new Date();
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const edit = (order) => {
    setSelected(order);
    setSaveError("");
    setOpen(true);
  };

  return (
    <Grid container spacing={3}>
      <OrderDialog
        open={open}
        order={selected}
        warehouses={warehouses}
        saving={saving}
        error={saveError}
        onClose={() => setOpen(false)}
        onSave={save}
        onRefresh={refreshSelected}
      />
      <Grid item xs={12}>
        <Breadcrumbs>
          <Link
            color="inherit"
            href={`${window.location.pathname.startsWith("/cpanel") ? "/cpanel" : ""}/manufacturing`}
          >
            {t("manufacturing.header")}
          </Link>
          <Typography>{t("manufacturing.orders")}</Typography>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Title>{t("manufacturing.orders")}</Title>
          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                disabled={loading || !warehouses.length}
                onClick={() => edit(null)}
              >
                {t("manufacturing.addOrder")}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Typography color="textSecondary">
                {t("manufacturing.ordersHint")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                variant="outlined"
                label={t("search")}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label={t("manufacturing.orderWarehouse")}
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
              >
                <MenuItem value="">
                  {t("manufacturing.allOrderWarehouses")}
                </MenuItem>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.label || warehouse.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                variant="outlined"
                type="month"
                InputLabelProps={{ shrink: true }}
                label={t("manufacturing.orderMonth")}
                value={month}
                onChange={(event) => setMonth(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label={t("manufacturing.orderStatus")}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <MenuItem value="">
                  {t("manufacturing.allOrderStatuses")}
                </MenuItem>
                {orderStatuses.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`manufacturing.orderStatuses.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          {error && (
            <Typography color="error" role="alert">
              {error}
            </Typography>
          )}
          {loading ? (
            <CircularProgress />
          ) : (
            <SortableTable
              header={t("manufacturing.orders")}
              tableData={filtered}
              rowsPerPage={25}
              defaultSortRow="dueDate"
              defaultOrder="asc"
              handleRowClick={edit}
              headCells={[
                { id: "uuid", label: t("manufacturing.orderNumber") },
                {
                  id: "Warehouse.name",
                  label: t("manufacturing.orderWarehouse"),
                },
                { id: "dueDate", label: t("manufacturing.orderDeadline") },
                { id: "status", label: t("manufacturing.orderStatus") },
                {
                  id: "ManufacturingOrderStockItems",
                  label: t("manufacturing.orderTargets"),
                },
              ]}
              specialDataFormatting={[
                {
                  id: "status",
                  format: (value) =>
                    t(`manufacturing.orderStatuses.${value}`, value || "—"),
                },
                {
                  id: "dueDate",
                  formatReturnsElement: true,
                  format: (value, _, order) => (
                    <Typography
                      color={
                        value &&
                        value < todayString &&
                        !["completed", "cancelled"].includes(order.status)
                          ? "error"
                          : "inherit"
                      }
                    >
                      {value || "—"}
                    </Typography>
                  ),
                },
                {
                  id: "Warehouse.name",
                  format: (value) =>
                    warehouses.find((v) => v.value === value).label,
                },
                {
                  id: "ManufacturingOrderStockItems",
                  formatReturnsElement: true,
                  format: (items) =>
                    (items || []).map((item) => (
                      <Typography key={item.id} variant="body2">
                        {item.StockItem?.sku} {item.StockItem?.name}:{" "}
                        {item.producedQuantity ?? 0} / {item.orderedQuantity}
                      </Typography>
                    )),
                },
              ]}
            />
          )}
          {!loading && !filtered.length && (
            <Typography>{t("manufacturing.noOrders")}</Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
